import { useEffect } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom'
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { api } from '../api/client'
import type { ProductDetailOut, ProductImagesOut } from '../api/types'
import { PageHeader } from '../components/Layout'
import { Kpi } from '../components/Kpi'
import { Pager } from '../components/Pager'
import { Panel } from '../components/States'
import { StatusChip } from '../components/StatusChip'
import { AXIS, ChartTip, GRID } from '../components/Charts'
import { bucketStarts, isoDate } from '../lib/dates'
import { useFilters, usePaging, useParam } from '../lib/filters'
import {
  branchName,
  count,
  formatDate,
  money,
  moneyCompact,
  monthLabel,
  num,
  percent,
  rangeLabel,
  shortDate,
} from '../lib/format'

const BUCKETS = ['auto', 'day', 'week', 'month'] as const
type Bucket = (typeof BUCKETS)[number]

export function ProductDetailPage() {
  const { sku = '' } = useParams()
  const { filters } = useFilters({ allTime: true })
  const [params, setParams] = useSearchParams()
  const location = useLocation()
  const [bucket, setBucket] = useParam<Bucket>('bucket', 'auto', BUCKETS)
  const family = params.get('family') === '1'
  // The HIKE id, when the list knew it — SKUs get edited in HIKE, ids don't.
  const productId = params.get('product')
  // No product at all: lines typed in at the till, found by their text. The
  // path then carries their name, not a SKU.
  const lineKey = productId ? null : params.get('line')
  // The list's own offset rides along in the URL for the way back.
  const { offset, limit, setOffset } = usePaging(25, 'invoices_offset')

  function toggleFamily(on: boolean) {
    const next = new URLSearchParams(params)
    if (on) next.set('family', '1')
    else next.delete('family')
    next.delete('invoices_offset')
    setParams(next, { replace: true })
  }

  const query = useQuery({
    queryKey: [
      'product',
      sku,
      productId,
      lineKey,
      filters.from,
      filters.to,
      filters.outletId,
      filters.scope,
      family,
      bucket,
      offset,
      limit,
    ],
    // Paging the invoices keeps this page on screen instead of blanking it,
    // which would lose the scroll position down at the invoices.
    placeholderData: keepPreviousData,
    queryFn: () =>
      api.get<ProductDetailOut>('/analytics/products/detail', {
        sku: lineKey ? null : sku,
        product_id: productId,
        line_key: lineKey,
        from_date: filters.from,
        to_date: filters.to,
        outlet_id: filters.outletId,
        scope: filters.scope,
        family,
        bucket,
        invoices_limit: limit,
        invoices_offset: offset,
      }),
  })

  // Asked for at the moment somebody looks, and never stored — see the
  // /products/image endpoint. A product without a photo is normal, so a
  // failure here must not disturb the page.
  const images = useQuery({
    queryKey: ['product-image', sku],
    // A typed-in line has no HIKE product to show.
    enabled: !lineKey,
    staleTime: 15 * 60 * 1000,
    retry: false,
    queryFn: () => api.get<ProductImagesOut>('/products/image', { sku }),
  })
  const photo = images.data?.primary

  const detail = query.data
  const stats = detail?.stats
  // The whole period, empty buckets included: a product selling now and then
  // otherwise shows its one or two sales as bars filling the chart.
  const sold = new Map((detail?.series ?? []).map((point) => [point.date, point]))
  // An open end runs from the first sale, or up to today: the span the API
  // sized the buckets for.
  const chartFrom = filters.from ?? stats?.first_sold ?? null
  const chartTo = filters.to ?? isoDate(new Date())
  const buckets =
    detail && chartFrom
      ? [...new Set([...bucketStarts(chartFrom, chartTo, detail.bucket), ...sold.keys()])].sort()
      : [...sold.keys()].sort()
  const series = buckets.map((date) => {
    const point = sold.get(date)
    return {
      label: detail?.bucket === 'month' ? monthLabel(date) : shortDate(date),
      revenue: point ? num(point.revenue) : 0,
      // A gap rather than zero, so the line doesn't slope into the days
      // either side of a sale as if it sold then too.
      units: point ? point.units : null,
    }
  })
  // Whole units, and no taller an axis than the data needs: left to itself,
  // one unit sold draws on a 0–4 scale, a quarter the height of its revenue.
  const unitsMin = Math.min(0, ...series.map((point) => point.units ?? 0))
  const unitsMax = Math.max(1, ...series.map((point) => point.units ?? 0))
  // With gaps in the line, a sale with no neighbours needs a dot to show.
  const isolated = (index: number) =>
    series[index]?.units != null &&
    series[index - 1]?.units == null &&
    series[index + 1]?.units == null

  // Both services deploy from one push but never land at the same instant,
  // so read the fields an older backend doesn't send defensively.
  const invoices = detail?.invoices ?? []
  const invoicesTotal = detail?.invoices_total ?? 0

  // The list's invoice count links here with #invoices: bring the card into
  // view once it has rendered, clear of the sticky top bar.
  useEffect(() => {
    if (location.hash !== '#invoices' || !detail) return
    const card = document.getElementById('invoices')
    if (!card) return
    const bar = document.querySelector('.topbar')?.getBoundingClientRect().height ?? 0
    window.scrollTo({ top: card.getBoundingClientRect().top + window.scrollY - bar - 12 })
  }, [location.hash, detail])
  const invoicesHref = `${location.pathname}${location.search}#invoices`

  // Back to the list with the same filters intact.
  const backSearch = new URLSearchParams(params)
  backSearch.delete('family')
  backSearch.delete('bucket')
  backSearch.delete('product')
  backSearch.delete('line')
  backSearch.delete('invoices_offset')

  return (
    <>
      <PageHeader title={sku} subtitle={rangeLabel(filters.from, filters.to)}>
        <Link className="btn" to={`/products?${backSearch.toString()}`}>
          ← All products
        </Link>
      </PageHeader>

      <div className="content stack">
        <div className="row">
          {/* Sets are found through SKUs, which a typed-in line doesn't have. */}
          {lineKey ? null : (
            <label className="row" style={{ gap: 6 }}>
              <input type="checkbox" checked={family} onChange={(e) => toggleFamily(e.target.checked)} />
              Include sets containing this product
            </label>
          )}

          <div className="seg" style={{ marginLeft: 'auto' }}>
            {BUCKETS.map((option) => (
              <button key={option} className={bucket === option ? 'on' : ''} onClick={() => setBucket(option)}>
                {option === 'auto' ? 'Auto' : option.charAt(0).toUpperCase() + option.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <Panel isLoading={query.isPending} error={query.error}>
          {stats && detail ? (
            <>
              {lineKey ? (
                <div className="alert info">
                  Not a catalogue product: these are the lines typed in at the till as{' '}
                  <strong>{detail.name ?? sku}</strong>, with no HIKE product attached.
                </div>
              ) : null}
              {detail.family && detail.family_root ? (
                <div className="alert info">
                  Including sets that contain this product, not just its own line.
                  Other <strong>{detail.family_root}</strong> products are excluded.
                </div>
              ) : null}

              <div className="grid cols-4">
                <Kpi label="Revenue" value={money(stats.revenue)} />
                <Kpi label="Units sold" value={count(stats.units)} />
                <Kpi
                  label="Invoices"
                  value={count(stats.invoice_count)}
                  to={stats.invoice_count > 0 ? invoicesHref : undefined}
                />
                <Kpi label="Customers" value={count(stats.distinct_customers)} />
                <Kpi
                  label="Average sold price"
                  value={stats.avg_sold_price === null ? '—' : money(stats.avg_sold_price)}
                />
                {stats.margin !== null ? (
                  <Kpi
                    label="Margin"
                    value={money(stats.margin)}
                    foot={
                      stats.cost_coverage_pct !== null && stats.cost_coverage_pct < 100
                        ? `${percent(stats.margin_pct)} · ${percent(stats.cost_coverage_pct, 0)} of lines costed`
                        : percent(stats.margin_pct)
                    }
                  />
                ) : null}
                <Kpi label="First sold" value={formatDate(stats.first_sold)} />
                <Kpi label="Last sold" value={formatDate(stats.last_sold)} />
              </div>

              {photo ? (
                <div className="card">
                  <div className="card-head">
                    <h2>Photo</h2>
                    <span className="hint">from HIKE</span>
                  </div>
                  <div className="card-body">
                    <img
                      src={photo.image_url}
                      alt={detail.sku}
                      loading="lazy"
                      style={{
                        // Portrait product shots would otherwise run the whole
                        // page height, so cap both dimensions and letterbox.
                        maxWidth: 260,
                        maxHeight: 240,
                        objectFit: 'contain',
                        borderRadius: 'var(--radius-sm)',
                        border: '1px solid var(--border)',
                        background: '#fff',
                      }}
                    />
                    {images.data && images.data.images.length > 1 ? (
                      <div className="row" style={{ gap: 8, marginTop: 10 }}>
                        {images.data.images.slice(1, 6).map((image) => (
                          <img
                            key={image.image_url}
                            src={image.thumbnail_url ?? image.image_url}
                            alt=""
                            loading="lazy"
                            style={{
                              width: 54,
                              height: 54,
                              objectFit: 'cover',
                              borderRadius: 4,
                              border: '1px solid var(--border)',
                            }}
                          />
                        ))}
                      </div>
                    ) : null}
                  </div>
                </div>
              ) : null}

              {detail.catalogue ? (
                <div className="card">
                  <div className="card-head">
                    <h2>Catalogue</h2>
                    {!detail.catalogue.is_active ? (
                      <span className="chip amber">Disabled in HIKE</span>
                    ) : null}
                  </div>
                  <div className="card-body">
                    <dl className="dl" style={{ maxWidth: 360 }}>
                      <dt>Retail price</dt>
                      <dd>
                        {detail.catalogue.retail_price === null
                          ? '—'
                          : money(detail.catalogue.retail_price)}
                      </dd>
                      <dt>Current cost</dt>
                      <dd>
                        {detail.catalogue.current_cost === null
                          ? '—'
                          : money(detail.catalogue.current_cost)}
                      </dd>
                    </dl>
                  </div>
                </div>
              ) : null}

              <div className="card" id="invoices">
                <div className="card-head">
                  <h2>Invoices</h2>
                  <span className="hint">
                    {count(invoicesTotal)} {filters.from || filters.to ? 'in the period' : 'all time'}
                  </span>
                </div>
                <div className="card-body flush">
                  <Panel
                    isLoading={false}
                    error={null}
                    isEmpty={invoices.length === 0}
                    emptyText="No invoices in this period."
                  >
                    <div className="table-wrap">
                      <table>
                        <thead>
                          <tr>
                            <th>Invoice</th>
                            <th className="num">Date</th>
                            <th>Customer</th>
                            <th>Branch</th>
                            <th>Salesperson</th>
                            <th>Status</th>
                            <th className="num">Units</th>
                            <th className="num">Revenue</th>
                          </tr>
                        </thead>
                        <tbody>
                          {invoices.map((row) => {
                            // A set's name with family on, or the product's
                            // name before a rename — otherwise it looks wrong.
                            const soldAs = row.item_names.filter((name) => name !== detail.name)
                            return (
                              <tr key={row.invoice_id}>
                                <td>
                                  <Link to={`/invoices/${row.invoice_id}`}>
                                    {row.hike_invoice_number ?? row.reference}
                                  </Link>
                                  {soldAs.length > 0 ? (
                                    <div className="cell-sub">as {soldAs.join(', ')}</div>
                                  ) : null}
                                </td>
                                <td className="num cell-sub">{formatDate(row.date)}</td>
                                <td>{row.customer_name}</td>
                                <td className="cell-sub" style={{ whiteSpace: 'nowrap' }}>
                                  {branchName(row.outlet_name)}
                                </td>
                                <td className="cell-sub">{row.sales_person ?? '—'}</td>
                                <td>
                                  <StatusChip status={row.status} />
                                </td>
                                <td className="num">{count(row.units)}</td>
                                <td className="num strong">{money(row.revenue)}</td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                    <Pager offset={offset} limit={limit} total={invoicesTotal} onChange={setOffset} />
                  </Panel>
                </div>
              </div>

              <div className="card">
                <div className="card-head">
                  <h2>Sales trend</h2>
                  <span className="hint">by {detail.bucket}</span>
                </div>
                <div className="card-body">
                  <Panel isLoading={false} error={null} isEmpty={sold.size === 0}>
                    <div className="chart tall">
                      <ResponsiveContainer>
                        <ComposedChart data={series} margin={{ top: 6, right: 12, bottom: 0, left: 6 }}>
                          <CartesianGrid {...GRID} vertical={false} />
                          <XAxis dataKey="label" {...AXIS} minTickGap={24} />
                          <YAxis
                            yAxisId="money"
                            {...AXIS}
                            width={62}
                            tickFormatter={(value) => moneyCompact(value)}
                          />
                          <YAxis
                            yAxisId="units"
                            orientation="right"
                            {...AXIS}
                            width={40}
                            allowDecimals={false}
                            // Pinned only while every whole unit gets a tick;
                            // past that, a round top keeps the ticks even.
                            domain={[unitsMin, unitsMax - unitsMin <= 4 ? unitsMax : 'auto']}
                            tickCount={Math.min(5, unitsMax - unitsMin + 1)}
                          />
                          <Tooltip
                            content={
                              <ChartTip
                                format={(name, value) =>
                                  name === 'Revenue' ? money(value) : count(value)
                                }
                              />
                            }
                            cursor={{ fill: 'var(--page)' }}
                          />
                          <Bar
                            yAxisId="money"
                            dataKey="revenue"
                            name="Revenue"
                            fill="var(--brand)"
                            radius={[3, 3, 0, 0]}
                            maxBarSize={40}
                          />
                          <Line
                            yAxisId="units"
                            type="monotone"
                            dataKey="units"
                            name="Units"
                            stroke="var(--blue)"
                            strokeWidth={2}
                            dot={({ cx, cy, index }: { cx?: number; cy?: number; index?: number }) =>
                              index !== undefined && isolated(index) ? (
                                <circle
                                  key={index}
                                  cx={cx}
                                  cy={cy}
                                  r={3.5}
                                  fill="var(--blue)"
                                  // Keeps it legible where it lands on a bar.
                                  stroke="var(--surface)"
                                  strokeWidth={1.5}
                                />
                              ) : (
                                <g key={index} />
                              )
                            }
                          />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                  </Panel>
                </div>
              </div>
            </>
          ) : null}
        </Panel>
      </div>
    </>
  )
}
